/**
 * Dagene i et bryllupsprogram.
 *
 * Et punkt vet ikke hvilken dato det skjer – det vet hvor mange dager unna
 * bryllupsdagen det er. Det er med vilje: flyttes bryllupet, følger mottakelsen
 * kvelden før med av seg selv. Her gjøres avviket om til noe et menneske kan
 * lese.
 */

/** Dagene skjemaet tilbyr. Dekker mottakelse kvelden før og brunsj dagen derpå. */
export const DAGSVALG = [-2, -1, 0, 1, 2] as const;

/** «Dagen før», «Bryllupsdagen», «2 dager etter» … */
export function dagsnavn(avvik: number): string {
  if (avvik === 0) return 'Bryllupsdagen';
  if (avvik === -1) return 'Dagen før';
  if (avvik === 1) return 'Dagen etter';
  const antall = Math.abs(avvik);
  return avvik < 0 ? `${antall} dager før` : `${antall} dager etter`;
}

/**
 * Selve datoen, når bryllupsdagen er bestemt.
 *
 * Regnet i UTC. Datoen er en ren kalenderdato uten klokkeslett, og å legge til
 * et døgn i lokal tid bommer med én dag rundt sommertidsskiftet.
 */
export function dagsdato(avvik: number, bryllupsdato: string | null | undefined): string | null {
  if (!bryllupsdato) return null;
  const dag = new Date(`${bryllupsdato}T00:00:00Z`);
  if (Number.isNaN(dag.getTime())) return null;
  dag.setUTCDate(dag.getUTCDate() + avvik);
  return dag.toLocaleDateString('nb-NO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

/**
 * Overskriften over en dag i programmet.
 *
 * Er bryllupsdagen bestemt, står ukedagen der: «Lørdag 20. mars». Det er det
 * folk sjekker mot kalenderen sin, og «dagen før» krever at man først finner ut
 * hvilken dag bryllupet er. Selve bryllupsdagen får et merke ved siden av – det
 * er den ene dagen der forholdet til de andre betyr noe.
 *
 * Uten dato finnes ingen ukedag, og da er det relative navnet det eneste vi
 * har. Et program kan settes opp før datoen er spikret.
 */
export function dagsoverskrift(
  avvik: number,
  bryllupsdato: string | null | undefined,
): { tittel: string; merke: string | null } {
  const dato = dagsdato(avvik, bryllupsdato);
  if (!dato) return { tittel: dagsnavn(avvik), merke: null };
  return {
    tittel: dato.charAt(0).toUpperCase() + dato.slice(1),
    merke: avvik === 0 ? 'Bryllupsdagen' : null,
  };
}

/** Grupperer punktene etter dag, i den rekkefølgen de kommer fra API-et. */
export function etterDag<T extends { dayOffset: number }>(punkter: T[]): [number, T[]][] {
  const dager = new Map<number, T[]>();
  for (const punkt of punkter) {
    const liste = dager.get(punkt.dayOffset);
    if (liste) liste.push(punkt);
    else dager.set(punkt.dayOffset, [punkt]);
  }
  return [...dager.entries()].sort(([a], [b]) => a - b);
}
